import React, { useEffect, useRef } from 'react';

interface GrainOverlayProps {
  /** Which edges to fade: 'bottom', 'top', 'both', or 'none' */
  fade?: 'bottom' | 'top' | 'both' | 'none';
  /** Show only the tinted background, no grain texture */
  noGrain?: boolean;
}

const maskMap = {
  bottom: 'linear-gradient(to bottom, black 0%, black 60%, transparent 100%)',
  top: 'linear-gradient(to bottom, transparent 0%, black 40%, black 100%)',
  both: 'linear-gradient(to bottom, transparent 0%, black 25%, black 75%, transparent 100%)',
  none: 'none',
};

const GrainOverlay: React.FC<GrainOverlayProps> = ({ fade = 'none', noGrain = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (noGrain) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const size = 128;
    canvas.width = size;
    canvas.height = size;

    const imageData = ctx.createImageData(size, size);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      const v = Math.random() * 255;
      data[i] = v;
      data[i + 1] = v;
      data[i + 2] = v;
      data[i + 3] = 18;
    }
    ctx.putImageData(imageData, 0, 0);
  }, [noGrain]);

  const mask = maskMap[fade];
  const maskStyle = fade !== 'none' ? { maskImage: mask, WebkitMaskImage: mask } : {};

  if (noGrain) {
    return (
      <div
        className="absolute inset-0 bg-black/[0.03] dark:bg-white/[0.02] pointer-events-none"
        style={maskStyle}
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full opacity-40 dark:opacity-25 mix-blend-multiply dark:mix-blend-soft-light pointer-events-none"
      style={{
        imageRendering: 'pixelated',
        width: '100%',
        height: '100%',
        ...maskStyle,
      }}
    />
  );
};

export default GrainOverlay;
